import React, { useState } from 'react';
import { Flex, TextField, ActionButton, Heading, Content, View } from '@adobe/react-spectrum';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { confirmSignUp, resendConfirmationCode } from '../../data/DynamoConnector';
import './VerifyEmail.css';

const friendlyError = (err) => {
    switch (err?.code) {
        case 'UserNotFoundException':
            return `We couldn't find an account with that email. Double-check it or sign up again.`;
        case 'InvalidParameterException':
            return err.message?.includes('already confirmed')
                ? `This email is already verified — you can log in.`
                : `That email doesn't look right.`;
        case 'NotAuthorizedException':
            return err.message?.includes('already confirmed')
                ? `This email is already verified — you can log in.`
                : err.message || `Request rejected by Cognito.`;
        case 'CodeMismatchException':
            return `That code doesn't match. Check the email and try again.`;
        case 'ExpiredCodeException':
            return `That code has expired. Request a new one.`;
        case 'LimitExceededException':
            return `Too many attempts. Wait a few minutes and try again.`;
        case 'TooManyRequestsException':
            return `Too many requests. Wait a moment and try again.`;
        default:
            return err?.message || `Something went wrong. Try again.`;
    }
};

export function VerifyEmail() {
    const auth = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [sendingCode, setSendingCode] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    const handleResend = async () => {
        if (!email.trim()) {
            toast(`Enter your email first`);
            return;
        }
        setSendingCode(true);
        try {
            await resendConfirmationCode(email);
            toast(`Verification code sent — check your inbox (and spam folder).`, { type: 'success' });
        } catch (err) {
            toast(friendlyError(err));
        } finally {
            setSendingCode(false);
        }
    };

    const handleConfirm = async () => {
        if (!email.trim() || !code.trim()) {
            toast(`Enter your email and the code you received`);
            return;
        }
        setConfirming(true);
        try {
            await confirmSignUp(email, code);
            setConfirmed(true);
            toast(`Email verified — you can log in now.`, { type: 'success' });
        } catch (err) {
            toast(friendlyError(err));
        } finally {
            setConfirming(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Verify Email - Commander Salt</title>
            </Helmet>
            <BreadCrumbs />
            <View padding="size-300" UNSAFE_className="verifyEmailWrapper">
                <Content>
                    <Heading level={2}>Verify your email</Heading>
                    <p className="verifyEmailLede">
                        If you signed up but never got a verification code — or the email landed in spam and the link
                        expired — this page will get you unstuck. Enter your email, get a fresh code, then confirm.
                    </p>

                    {confirmed ? (
                        <Flex direction="column" gap="size-200" marginTop="size-300">
                            <p className="verifyEmailSuccess">
                                ✅ Your email is verified. You can now log in to your account.
                            </p>
                            <ActionButton
                                onPress={() => {
                                    navigate('/');
                                    void auth.signinRedirect();
                                }}
                            >
                                Go to login
                            </ActionButton>
                        </Flex>
                    ) : (
                        <Flex direction="column" gap="size-200" marginTop="size-300">
                            <TextField
                                label="Email"
                                value={email}
                                onChange={setEmail}
                                type="email"
                                autoComplete="email"
                                width="100%"
                                maxWidth="size-4600"
                            />
                            <Flex gap="size-100" wrap>
                                <ActionButton onPress={handleResend} isDisabled={sendingCode}>
                                    {sendingCode ? 'Sending…' : 'Send me a code'}
                                </ActionButton>
                            </Flex>

                            <View marginTop="size-300">
                                <p className="verifyEmailSubhead">Already have a code?</p>
                                <Flex direction="column" gap="size-200">
                                    <TextField
                                        label="Verification code"
                                        value={code}
                                        onChange={setCode}
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        width="100%"
                                        maxWidth="size-4600"
                                    />
                                    <Flex gap="size-100" wrap>
                                        <ActionButton onPress={handleConfirm} isDisabled={confirming}>
                                            {confirming ? 'Confirming…' : 'Confirm email'}
                                        </ActionButton>
                                    </Flex>
                                </Flex>
                            </View>
                        </Flex>
                    )}
                </Content>
            </View>
        </>
    );
}
